import {
    Box,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
} from "@mui/material"
import { ReactElement } from "react"

import UserRow from "./activeUsersRows"
import { IDENTIFIER_TYPE_DNO_TAG } from "../../../constants/roles"
import { useGetUserData } from "../../../hooks/useGetUserData"
import useActiveUsers from "../../../services/middleware/hooks/adminPortal/useActiveUsers"
import { TUsersOutput } from "../../../services/middleware/mappers/adminPortal/activeUsersMapper"
import AppLoadingButton from "../../buttons/appLoadingButton"
import ZeroActiveUsersFoundMsg from "../../fallback/zeroActiveUsersFoundMsg"
import AppTooltip from "../../helper/appTooltip"
import AppSkeleton from "../../loading/skeletons/appSkeleton"
import RevalidatingSpinner from "../../loading/spinners/revalidatingSpinner"
import AppPaper from "../../surfaces/appPaper"
import AppTableCell from "../appTableCell"
import AppTableContentRow from "../appTableContentRow"

interface IHeading {
    title: string
    center: boolean
    enableSort: boolean
    showPermission: boolean
}

interface IActiveUserProps {
    filter: string | null
    search: string
    mode: string
    isActive: boolean
}

const headings: IHeading[] = [
    {
        title: "Name",
        center: false,
        enableSort: true,
        showPermission: true,
    },
    {
        title: "E-Mail",
        center: false,
        enableSort: false,
        showPermission: true,
    },
    {
        title: "MFA Status",
        center: true,
        enableSort: false,
        showPermission: true,
    },
    {
        title: "Administrator",
        center: true,
        enableSort: false,
        showPermission: true,
    },
    {
        title: "Application Reviewer",
        center: true,
        enableSort: false,
        showPermission: false,
    },
    {
        title: "Threshold Manager",
        center: true,
        enableSort: false,
        showPermission: false,
    },
    {
        title: "Reset MFA",
        center: true,
        enableSort: false,
        showPermission: true,
    },
    {
        title: "Remove User",
        center: true,
        enableSort: false,
        showPermission: true,
    },
]

/**
 * Displays table with all active users
 * <ActiveUserResults />
 */
const ActiveUserResults = ({
    filter,
    search,
    mode,
    isActive,
}: IActiveUserProps): ReactElement => {
    const {
        users: activeUsers,
        isLoading,
        isLoadingMore,
        isValidating,
        hasMore,
        loadMore,
        mutate,
    } = useActiveUsers(true, filter, search, isActive)

    const { isDnoAdmin, isEnaSysAdmin } = useGetUserData()

    const displayHeadings =
        isDnoAdmin ||
        (isEnaSysAdmin && !!filter?.startsWith(IDENTIFIER_TYPE_DNO_TAG))
            ? headings
            : headings.filter((heading) => heading.showPermission)

    /**
     * Returns flag denoting if no search results returned
     * @returns {boolean}
     */
    const emptySearch = (): boolean => {
        if (isLoading) {
            return false
        }
        if (Array.isArray(activeUsers)) {
            return activeUsers.length === 0
        }
        if (!activeUsers) {
            return true
        }
        return false
    }

    return emptySearch() ? (
        <AppPaper>
            <ZeroActiveUsersFoundMsg />
        </AppPaper>
    ) : (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        <AppTableCell>
                            <RevalidatingSpinner isValidating={isValidating} />
                        </AppTableCell>
                        {displayHeadings.map((heading: IHeading) => (
                            <AppTooltip
                                title={heading.title}
                                key={heading.title}
                            >
                                <AppTableCell
                                    align={heading.center ? "center" : "left"}
                                    sx={{
                                        paddingTop: 0,
                                        cursor: heading.enableSort
                                            ? "pointer"
                                            : null,
                                    }}
                                >
                                    <TableSortLabel>
                                        <Typography
                                            variant="body1"
                                            fontWeight="medium"
                                            color="primary"
                                        >
                                            {heading.title}
                                        </Typography>
                                    </TableSortLabel>
                                </AppTableCell>
                            </AppTooltip>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {isLoading
                        ? Array.from(Array(20).keys()).map((v) => (
                              <AppTableContentRow
                                  hover
                                  key={`SearchTableSkeleton${v}`}
                              >
                                  <AppTableCell>
                                      <AppSkeleton />
                                  </AppTableCell>
                                  <AppTableCell>
                                      <AppSkeleton />
                                  </AppTableCell>
                                  <AppTableCell>
                                      <AppSkeleton />
                                  </AppTableCell>
                                  <AppTableCell>
                                      <AppSkeleton />
                                  </AppTableCell>
                                  <AppTableCell>
                                      <AppSkeleton />
                                  </AppTableCell>
                                  <AppTableCell>
                                      <AppSkeleton />
                                  </AppTableCell>
                                  <AppTableCell>
                                      <AppSkeleton />
                                  </AppTableCell>
                              </AppTableContentRow>
                          ))
                        : (activeUsers as TUsersOutput).map((user) => (
                              <UserRow
                                  key={user.userName}
                                  user={user}
                                  mutate={mutate}
                                  filter={filter}
                                  mode={mode}
                              />
                          ))}
                </TableBody>
            </Table>
            {hasMore && (
                <Box display="flex" justifyContent="center" py={2}>
                    <AppLoadingButton
                        variant="outlined"
                        loading={isLoadingMore}
                        onClick={loadMore}
                    >
                        Load More
                    </AppLoadingButton>
                </Box>
            )}
        </TableContainer>
    )
}

export default ActiveUserResults
